import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, VStack, HStack, Text, Heading, Button, Icon, useToast, useColorModeValue,
  Spinner, useDisclosure, Divider, FormControl, FormLabel, FormHelperText, Input,
  Switch, NumberInput, NumberInputField, Checkbox, CheckboxGroup,
  Alert, AlertIcon, AlertDescription, Code, Tag, TagLabel,
} from '@chakra-ui/react';
import { FiPlus, FiClock, FiSliders, FiUser } from 'react-icons/fi';
import { httpsCallable } from 'firebase/functions';
import { functionsUsEast1 } from '../../../utils/init-firebase';
import { RuleEditorModal, RuleTable, blankRule } from '../Admin/AriveSyncRulesAdmin';

const COMMON_CRON_PRESETS = [
  { label: 'Every 30 minutes', value: '*/30 * * * *' },
  { label: 'Every hour',       value: '0 * * * *' },
  { label: 'Every 2 hours',    value: '0 */2 * * *' },
  { label: 'Every 4 hours',    value: '0 */4 * * *' },
  { label: 'Every 8 hours',    value: '0 */8 * * *' },
  { label: 'Daily at 6am ET',  value: '0 6 * * *' },
];

export default function AriveSyncUserPanel({ userId }) {
  const toast = useToast();
  const cardBG       = useColorModeValue('gray.50', 'gray.700');
  const borderColor  = useColorModeValue('gray.200', 'gray.700');
  const tableHeaderBG = useColorModeValue('gray.100', 'gray.800');

  const [settings, setSettings] = useState(null);
  const [allRules, setAllRules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null);

  const [activeRule, setActiveRule] = useState(null);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [savingRule, setSavingRule] = useState(false);

  const load = useCallback(async () => {
    if (!userId) return;
    setLoading(true);
    try {
      const getSettings = httpsCallable(functionsUsEast1, 'getAriveSyncUserSettings');
      const getRules = httpsCallable(functionsUsEast1, 'getAriveSyncRules');
      const [s, r] = await Promise.all([
        getSettings({ targetUserId: userId }),
        getRules({}),
      ]);
      setSettings(s.data?.settings || null);
      setAllRules(r.data?.rules || []);
    } catch (err) {
      toast({ title: 'Failed to load sync settings', description: err.message, status: 'error', duration: 4000 });
    } finally {
      setLoading(false);
    }
  }, [userId, toast]);

  useEffect(() => { load(); }, [load]);

  const updateSetting = async (field, value) => {
    setSaving(field);
    try {
      const fn = httpsCallable(functionsUsEast1, 'updateAriveSyncUserSettings');
      const res = await fn({ targetUserId: userId, [field]: value });
      setSettings(res.data?.settings || null);
    } catch (err) {
      toast({ title: 'Save failed', description: err.message, status: 'error', duration: 4000 });
    } finally {
      setSaving(null);
    }
  };

  const handleSaveRule = async (draft) => {
    setSavingRule(true);
    try {
      const fn = httpsCallable(functionsUsEast1, 'upsertAriveSyncRule');
      await fn({ ...draft, scope: 'user', owner_id: userId });
      toast({ title: draft.id ? 'Rule updated' : 'Rule created', status: 'success', duration: 2000 });
      onClose();
      await load();
    } catch (err) {
      toast({ title: 'Save failed', description: err.message, status: 'error', duration: 4000 });
    } finally {
      setSavingRule(false);
    }
  };

  const handleDeleteRule = async (rule) => {
    if (!window.confirm(`Delete rule "${rule.metadata?.description || rule.id}"?`)) return;
    try {
      const fn = httpsCallable(functionsUsEast1, 'deleteAriveSyncRule');
      await fn({ id: rule.id });
      toast({ title: 'Rule deleted', status: 'info', duration: 2000 });
      await load();
    } catch (err) {
      toast({ title: 'Delete failed', description: err.message, status: 'error', duration: 4000 });
    }
  };

  const openNew = () => {
    setActiveRule({ ...blankRule('user', userId), scope: 'user', owner_id: userId });
    onOpen();
  };
  const openEdit = (rule) => {
    setActiveRule(rule);
    onOpen();
  };

  const myRules = allRules.filter((r) => r.scope === 'user' && r.owner_id === userId);
  const companyRules = allRules.filter((r) => r.scope === 'company');
  const disabledIds = settings?.disabled_company_rule_ids || [];

  if (loading) {
    return <Box p={6} bg={cardBG} borderRadius="lg" borderWidth="1px" borderColor={borderColor}><Spinner size="sm" /></Box>;
  }

  return (
    <VStack align="stretch" spacing={4}>
      {/* Scheduled Pulls */}
      <Box p={5} bg={cardBG} borderRadius="lg" borderWidth="1px" borderColor={borderColor}>
        <HStack mb={4} spacing={2}>
          <Icon as={FiClock} color="purple.500" />
          <Heading size="sm">Scheduled Pipeline Pulls</Heading>
        </HStack>
        <Text fontSize="xs" color="gray.500" mb={4}>
          Automatically pull new loans/leads from your Arive pipeline on a schedule (requires inbound auto-create to also be on).
        </Text>
        <VStack align="stretch" spacing={3}>
          <HStack justify="space-between" p={3} borderWidth="1px" borderRadius="md" borderColor={borderColor}>
            <VStack align="flex-start" spacing={0}>
              <Text fontSize="sm" fontWeight="semibold">Enable scheduled pulls</Text>
              <Text fontSize="xs" color="gray.500">When ON, the dispatcher will check your Arive pipeline on the schedule below.</Text>
            </VStack>
            <Switch
              size="md"
              isChecked={settings?.scheduled_pull_enabled === true}
              isDisabled={saving === 'scheduled_pull_enabled'}
              onChange={(e) => updateSetting('scheduled_pull_enabled', e.target.checked)}
            />
          </HStack>
          {settings?.scheduled_pull_enabled && (
            <>
              <FormControl>
                <FormLabel fontSize="xs">Schedule (cron format)</FormLabel>
                <HStack spacing={2}>
                  <Input
                    size="sm"
                    value={settings?.scheduled_pull_schedule || '0 */4 * * *'}
                    onChange={(e) => setSettings({ ...settings, scheduled_pull_schedule: e.target.value })}
                    onBlur={(e) => updateSetting('scheduled_pull_schedule', e.target.value)}
                    fontFamily="mono"
                  />
                </HStack>
                <FormHelperText fontSize="xs">
                  Presets:{' '}
                  {COMMON_CRON_PRESETS.map((p, i) => (
                    <React.Fragment key={p.value}>
                      {i > 0 && ' · '}
                      <Button
                        size="xs"
                        variant="link"
                        onClick={() => updateSetting('scheduled_pull_schedule', p.value)}
                      >{p.label}</Button>
                    </React.Fragment>
                  ))}
                </FormHelperText>
              </FormControl>
              <FormControl>
                <FormLabel fontSize="xs">Lookback (hours)</FormLabel>
                <NumberInput
                  size="sm"
                  value={settings?.scheduled_pull_lookback_h || 24}
                  min={1}
                  max={168}
                  onChange={(_s, n) => setSettings({ ...settings, scheduled_pull_lookback_h: Number.isFinite(n) ? n : 24 })}
                  onBlur={() => updateSetting('scheduled_pull_lookback_h', settings.scheduled_pull_lookback_h)}
                >
                  <NumberInputField />
                </NumberInput>
                <FormHelperText fontSize="xs">How far back to look for new Arive entities each pull.</FormHelperText>
              </FormControl>
            </>
          )}
        </VStack>
      </Box>

      {/* Default Imports */}
      <Box p={5} bg={cardBG} borderRadius="lg" borderWidth="1px" borderColor={borderColor}>
        <HStack mb={4} spacing={2}>
          <Icon as={FiSliders} color="purple.500" />
          <Heading size="sm">Inbound Import Defaults</Heading>
        </HStack>
        <Text fontSize="xs" color="gray.500" mb={4}>
          Defaults applied to deals auto-created from Arive when an inbound rule doesn't override them.
        </Text>
        <VStack align="stretch" spacing={3}>
          <FormControl>
            <FormLabel fontSize="xs">Default Origin stage</FormLabel>
            <Input
              size="sm"
              value={settings?.default_origin_stage || 'lead-new'}
              onChange={(e) => setSettings({ ...settings, default_origin_stage: e.target.value })}
              onBlur={(e) => updateSetting('default_origin_stage', e.target.value)}
              placeholder="lead-new"
            />
            <FormHelperText fontSize="xs">Slug form (e.g., lead-new, loan-application-intake).</FormHelperText>
          </FormControl>
          <FormControl>
            <FormLabel fontSize="xs">Default owner (Firebase uid)</FormLabel>
            <Input
              size="sm"
              value={settings?.default_origin_owner || ''}
              onChange={(e) => setSettings({ ...settings, default_origin_owner: e.target.value || null })}
              onBlur={() => updateSetting('default_origin_owner', settings.default_origin_owner)}
              placeholder="Leave blank = use originator from Arive"
            />
          </FormControl>
        </VStack>
      </Box>

      {/* Opt-out of company rules */}
      <Box p={5} bg={cardBG} borderRadius="lg" borderWidth="1px" borderColor={borderColor}>
        <HStack mb={4} spacing={2}>
          <Icon as={FiSliders} color="purple.500" />
          <Heading size="sm">Company Rule Opt-Outs</Heading>
        </HStack>
        <Text fontSize="xs" color="gray.500" mb={4}>
          Check a rule to opt yourself out of it. Your personal rules below ALWAYS win over company rules at the same trigger.
        </Text>
        {companyRules.length === 0 ? (
          <Text fontSize="xs" color="gray.500">No company rules to opt out of.</Text>
        ) : (
          <CheckboxGroup
            value={disabledIds}
            onChange={(values) => updateSetting('disabled_company_rule_ids', values)}
          >
            <VStack align="stretch" spacing={2}>
              {companyRules.map((rule) => (
                <Checkbox key={rule.id} value={rule.id} size="sm">
                  <HStack>
                    <Text fontSize="sm">{rule.metadata?.description || <em>(no description)</em>}</Text>
                    <Tag size="sm" colorScheme={rule.direction === 'outbound' ? 'blue' : 'purple'}>
                      <TagLabel>{rule.direction}</TagLabel>
                    </Tag>
                    <Code fontSize="10px">{rule.trigger_type}</Code>
                  </HStack>
                </Checkbox>
              ))}
            </VStack>
          </CheckboxGroup>
        )}
      </Box>

      {/* Personal rules CRUD */}
      <Box p={5} bg={cardBG} borderRadius="lg" borderWidth="1px" borderColor={borderColor}>
        <HStack mb={4} justify="space-between">
          <HStack spacing={2}>
            <Icon as={FiUser} color="purple.500" />
            <Heading size="sm">My Personal Sync Rules</Heading>
          </HStack>
          <Button size="sm" leftIcon={<Icon as={FiPlus} />} colorScheme="blue" onClick={openNew}>
            Add rule
          </Button>
        </HStack>
        <Text fontSize="xs" color="gray.500" mb={4}>
          Personal rules apply only to your deals and always override company rules at the same trigger.
        </Text>
        <RuleTable
          rules={myRules}
          onEdit={openEdit}
          onDelete={handleDeleteRule}
          headerBG={tableHeaderBG}
        />
      </Box>

      <Alert status="info" borderRadius="md" fontSize="xs">
        <AlertIcon />
        <AlertDescription>
          Admins manage company-wide rules in <strong>Admin &raquo; Arive Sync Rules</strong>.
        </AlertDescription>
      </Alert>

      <RuleEditorModal
        isOpen={isOpen}
        onClose={onClose}
        rule={activeRule}
        onSave={handleSaveRule}
        saving={savingRule}
        lockScope="user"
      />
    </VStack>
  );
}
